import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./TwofoldPaywall.module.scss"

/**
 * TwofoldPaywall (`/twofold`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: FEATURE-SPECIFIC.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — this screen sells ONE
 * real security feature and nothing else: REQUIRE PASSCODE + FACE ID (Dual Lock).
 *
 * ── The one feature ────────────────────────────────────────────────────────
 * Dual Lock: turn on "Require Passcode + Face ID" and your vault demands BOTH a
 * code you know AND a face that is you — two independent locks in series — every
 * single time it opens. This is the single locked feature the screen is opened
 * from; everything on it is about that one capability.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "One lock can be guessed. Two can't." A code can be shoulder-surfed, watched,
 * or handed over under pressure. A second lock — your face — is a second,
 * independent gate the same secret can't cross. The whole screen is an honest
 * AND-gate: to reach your memories a visitor must clear BOTH bolts. The drama is
 * NOT alarm — it's the calm, solid stillness of a second bolt that simply will
 * not move for a stranger who already has your code. Composed, ironclad, sleep-
 * easy. The interactive proof is a CONTRAST the user drives: "Someone else" vs
 * "You" at the same gate — the same code, two completely different outcomes.
 *
 * ── Divergence (vs. the 90+ set, incl. the security cluster) ────────────────
 * Dual Lock has never carried its own paywall — it appears only as one chip in a
 * 4–6 guard checklist (Ward/Sanctum/Clasp…) or a "2nd lock snaps shut" tier
 * detail (Trove/Tumbler). Nothing here repeats the set:
 * Layout      : a vertical TWO-BOLT GATE (outer passcode bolt over inner Face-ID
 *               bolt) with a conduction SPINE that only completes when both pass,
 *               your memory wall behind it — not a single vault door, keypad
 *               grid, dial, wall, window, journal, cord, phone+ledger or console.
 * Interaction : ARM the second lock, then drive a "Who's at the gate?" CONTRAST
 *               (Someone else / You) — the same code branches to HELD vs OPEN.
 *               New verb to the set: an AND-gate pass-through where factor one
 *               SUCCEEDS for the intruder and factor two blocks (the opposite of
 *               Sentry, where the intruder FAILS the code and is photographed).
 * Metaphor    : two bolts in series / defense-in-depth — a circuit that conducts
 *               to your memories only when BOTH locks clear.
 * Motion      : the signature beat is a bolt that REFUSES to move — it nudges and
 *               springs back with a solid "held" settle; then, for you, both
 *               bolts glide open together in a synced double-retract.
 * Value frame : RISK REVERSAL + CONTRAST — "even if they get your code, they
 *               still aren't you," one-lock-vs-two shown side by side.
 * Register    : engineered peace of mind — the click of a second deadbolt. Calm,
 *               not fear (not Caught's CCTV dread); redundancy, not surveillance
 *               (not Sentry's night-watch).
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * "Require Passcode + Face ID" is a REAL Private Photo Vault feature; the screen
 * states plainly what it does (demands both a code and your face to open) and
 * nothing it doesn't. The visitor is clearly an EXAMPLE demonstration, never a
 * claim the user has been targeted. Being feature-specific, nothing is at stake:
 * the second lock only changes who can OPEN the app — your memories never move
 * and nothing is ever deleted. No countdown, no fake scarcity. "Save 79%" is
 * real arithmetic from live product data. Yearly is the featured, default, most
 * prominent price; monthly is small and secondary. The 7-day free trial is the
 * real trial. Renders on built-in debug product data with bundled photos — no
 * native injection, no external egress. Honours reduced motion (arrives armed
 * and held, no looping animation).
 */

// The memory wall behind the gate — bundled, self-contained, reused from the
// repo. Frosted while locked; blooms only when both locks are yours.
const WALL = [
  "/byheart/m01.jpg",
  "/byheart/m02.jpg",
  "/byheart/m03.jpg",
  "/byheart/m05.jpg",
  "/byheart/m07.jpg",
  "/byheart/m09.jpg",
]

// The one feature, in its two honest halves + the sum.
const FACTORS = [
  {
    key: "know",
    Icon: svg.featureIcons.PatternLock,
    name: "Something you know",
    line: "Your passcode or pattern — the secret only in your head.",
  },
  {
    key: "are",
    Icon: svg.featureIcons.SecurityPlus,
    name: "Something you are",
    line: "Your Face ID or Touch ID — a lock a stranger can’t borrow.",
  },
  {
    key: "both",
    Icon: svg.featureIcons.FacedownLock,
    name: "Both, every time",
    line: "The vault opens only when the two clear together. No exceptions.",
  },
]

type Who = "unknown" | "you"

const TwofoldPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  // Warm the safe-area insets to the gate backdrop so the screen reads as one
  // continuous surface top-to-bottom.
  useEffect(() => {
    const dark = state.initialData?.darkMode
    actions.setHeaderInsetColor(dark ? "#0d131f" : "#0d1524")
    actions.setFooterInsetColor(dark ? "#0d131f" : "#0d1524")
  }, [actions, state.initialData?.darkMode])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the dual-lock state machine ─────────────────────────────────────────
  // armed = the second lock (Face ID) is required; who = who is at the gate;
  // dots = code digits entered; bolt1 = outer (code) bolt open; bolt2 = inner
  // (face) bolt state; scan = the face check; revealed = the wall is uncovered.
  const [armed, setArmed] = useState(true)
  const [who, setWho] = useState<Who>("unknown")
  const [dots, setDots] = useState(0)
  const [bolt1, setBolt1] = useState(false)
  const [bolt2, setBolt2] = useState<"shut" | "held" | "open">("shut")
  const [scan, setScan] = useState<"idle" | "scan" | "denied" | "granted">(
    "idle",
  )
  const [revealed, setRevealed] = useState(false)
  const [held, setHeld] = useState(false) // one-shot "held" recoil pulse

  const timers = useRef<number[]>([])
  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }
  const after = (ms: number, fn: () => void) =>
    timers.current.push(window.setTimeout(fn, ms))

  // Snap straight to the resolved end-state for a given (armed, who) — used for
  // reduced motion and as the settle target.
  const settle = useCallback((isArmed: boolean, w: Who) => {
    setDots(4)
    setBolt1(true)
    if (!isArmed) {
      setScan("idle")
      setBolt2("shut")
      setRevealed(true) // one lock: whoever has the code is in
    } else if (w === "you") {
      setScan("granted")
      setBolt2("open")
      setRevealed(true)
    } else {
      setScan("denied")
      setBolt2("held")
      setRevealed(false) // second lock holds the stranger out
    }
  }, [])

  // Play the beat for a given (armed, who): everyone in this demo HAS the code
  // — that is the whole point. The code opens bolt one; only the second lock
  // tells friend from stranger.
  const play = useCallback(
    (isArmed: boolean, w: Who) => {
      clearTimers()
      setHeld(false)
      setDots(0)
      setBolt1(false)
      setBolt2("shut")
      setScan("idle")
      setRevealed(false)

      if (reduceMotion) {
        settle(isArmed, w)
        return
      }

      // the code is entered, digit by digit
      after(240, () => setDots(1))
      after(420, () => setDots(2))
      after(600, () => setDots(3))
      after(780, () => setDots(4))
      // code accepted → outer bolt slides open
      after(1050, () => setBolt1(true))

      if (!isArmed) {
        // no second lock — the wall is open to whoever cleared the code
        after(1480, () => setRevealed(true))
        return
      }

      // second lock engages: the face is checked
      after(1180, () => setScan("scan"))
      if (w === "you") {
        after(1820, () => {
          setScan("granted")
          setBolt2("open")
        })
        after(2220, () => setRevealed(true))
      } else {
        after(1820, () => {
          setScan("denied")
          setBolt2("held")
          setHeld(true)
        })
        after(2120, () => setHeld(false))
      }
    },
    [reduceMotion, settle],
  )

  // Replay whenever the two user-driven inputs change.
  useEffect(() => {
    play(armed, who)
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, who, reduceMotion])

  const toggleArm = useCallback(() => setArmed((a) => !a), [])
  const replay = useCallback(() => play(armed, who), [play, armed, who])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // Human-readable status under the gate, derived from the resolved state.
  const status = (() => {
    if (!armed) {
      return revealed
        ? who === "you"
          ? "One lock — and it let you in on a code alone."
          : "One lock — and your code was the whole story."
        : "One lock. Whoever has your code is one tap away."
    }
    if (who === "you") {
      return revealed
        ? "Both locks are yours. You’re in."
        : "Two locks — the code, then your face…"
    }
    return bolt2 === "held"
      ? "They had your code. They weren’t you."
      : "Two locks — the code cleared, now the face…"
  })()

  const rootCls = [
    styles.root,
    armed ? styles.armed : styles.single,
    revealed ? styles.revealed : "",
    held ? styles.held : "",
    who === "you" ? styles.you : styles.unknown,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={rootCls}>
      <span className={styles.aura} aria-hidden="true" />
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.2 11.7l2 2 3.6-3.9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Require Passcode + Face ID
          </span>
          <h1 className={styles.title}>
            One lock can be guessed.
            <span className={styles.titleAccent}> Two can’t.</span>
          </h1>
          <p className={styles.sub}>
            A code can be watched, shared, or handed over. Add a second lock —
            your face — and even someone who has your passcode still meets a
            gate that only opens for you.
          </p>
        </section>

        {/* ── the gate: two bolts in series over your memory wall ────────── */}
        <section className={styles.stage}>
          <div className={styles.gate} aria-hidden="true">
            {/* the conduction spine — lights only as each lock clears */}
            <span className={styles.spine}>
              <span
                className={`${styles.seg} ${styles.segEnter} ${
                  dots >= 4 ? styles.segLive : ""
                }`}
              />
              <span
                className={`${styles.seg} ${styles.segMid} ${
                  bolt1 ? styles.segLive : ""
                }`}
              />
              <span
                className={`${styles.seg} ${styles.segInner} ${
                  bolt2 === "open" ? styles.segLive : ""
                }`}
              />
            </span>

            {/* who is at the gate */}
            <div className={styles.visitor}>
              <span className={styles.visitorAv}>
                {who === "you" ? (
                  <svg viewBox="0 0 24 24" width="17" height="17">
                    <circle
                      cx="12"
                      cy="8.4"
                      r="3.7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                    <path
                      d="M5.5 19.4c0-3.6 2.9-5.8 6.5-5.8s6.5 2.2 6.5 5.8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="17" height="17">
                    <circle
                      cx="12"
                      cy="8.4"
                      r="3.7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeDasharray="2.2 2.2"
                    />
                    <path
                      d="M5.5 19.4c0-3.6 2.9-5.8 6.5-5.8s6.5 2.2 6.5 5.8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeDasharray="2.2 2.2"
                    />
                  </svg>
                )}
              </span>
              <span className={styles.visitorName}>
                {who === "you" ? "You" : "Unknown"}
              </span>
              <span className={styles.visitorHint}>has the code</span>
            </div>

            {/* outer lock — your passcode */}
            <div className={`${styles.lock} ${bolt1 ? styles.lockOpen : ""}`}>
              <span className={styles.lockLabel}>Passcode</span>
              <span className={styles.dots}>
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={`${styles.dot} ${i < dots ? styles.dotOn : ""}`}
                  />
                ))}
              </span>
              <span className={styles.bolt}>
                <span className={styles.boltBar} />
                <span className={styles.boltState}>
                  {bolt1 ? "open" : "shut"}
                </span>
              </span>
            </div>

            {/* inner lock — your face */}
            <div
              className={`${styles.lock} ${styles.lockFace} ${
                bolt2 === "open" ? styles.lockOpen : ""
              } ${bolt2 === "held" ? styles.lockHeld : ""}`}
            >
              <span className={styles.lockLabel}>Face ID</span>
              <span className={`${styles.face} ${styles["face_" + scan]}`}>
                <span className={styles.faceRing} />
                <span className={styles.faceScan} />
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <circle cx="9.2" cy="10" r="1" fill="currentColor" />
                  <circle cx="14.8" cy="10" r="1" fill="currentColor" />
                  <path
                    d="M12 9.6v3.1l-1.1 1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.4 15.2c1.6 1 3.6 1 5.2 0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={styles.bolt}>
                <span className={styles.boltBar} />
                <span className={styles.boltState}>
                  {bolt2 === "open"
                    ? "open"
                    : bolt2 === "held"
                      ? "held"
                      : armed
                        ? "shut"
                        : "off"}
                </span>
              </span>
              {!armed && <span className={styles.lockOff}>no second lock</span>}
            </div>

            {/* the memory wall behind the gate */}
            <div className={styles.wall}>
              {WALL.map((src, i) => (
                <span
                  key={i}
                  className={styles.tile}
                  style={{ backgroundImage: `url(${src})` }}
                />
              ))}
              <span className={styles.veil}>
                <span className={styles.crest}>
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <rect
                      x="5"
                      y="10.5"
                      width="14"
                      height="9.5"
                      rx="2.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                    <path
                      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>Private</span>
                </span>
              </span>
              <span className={styles.exposeTag}>Exposed</span>
            </div>
          </div>

          {/* status */}
          <span className={styles.status} aria-live="polite">
            <span className={styles.statusDot} aria-hidden="true" />
            {status}
          </span>

          {/* who's-at-the-gate contrast control */}
          <div
            className={styles.whoRow}
            role="group"
            aria-label="Who's at the gate"
          >
            <span className={styles.whoLabel}>Who’s at the gate?</span>
            <div className={styles.whoSeg}>
              <button
                className={`${styles.whoBtn} ${
                  who === "unknown" ? styles.whoSel : ""
                }`}
                onClick={() => setWho("unknown")}
                aria-pressed={who === "unknown"}
              >
                Someone else
              </button>
              <button
                className={`${styles.whoBtn} ${
                  who === "you" ? styles.whoSel : ""
                }`}
                onClick={() => setWho("you")}
                aria-pressed={who === "you"}
              >
                You
              </button>
            </div>
            <button
              className={styles.replay}
              onClick={replay}
              aria-label="Replay"
            >
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M4.5 12a7.5 7.5 0 1 1 2.2 5.3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <path
                  d="M4.2 17.6l.4-3.6 3.6.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* the decisive affordance — arm the second lock */}
          <button
            className={`${styles.armBtn} ${armed ? styles.armOn : ""}`}
            onClick={toggleArm}
            aria-pressed={armed}
          >
            <span className={styles.armSwitch} aria-hidden="true">
              <span className={styles.armKnob} />
            </span>
            <span className={styles.armText}>
              {armed
                ? "Second lock on — Passcode + Face ID"
                : "Add the second lock"}
            </span>
          </button>
        </section>

        {/* ── the one feature, in detail ─────────────────────────────────── */}
        <ul className={styles.factors}>
          <li className={styles.factorsHead}>What the second lock adds</li>
          {FACTORS.map((f, i) => (
            <li
              key={f.key}
              className={styles.factor}
              style={{ ["--d" as any]: i }}
            >
              <span className={styles.factorIcon} aria-hidden="true">
                <f.Icon width={18} height={18} />
              </span>
              <span className={styles.factorText}>
                <span className={styles.factorName}>{f.name}</span>
                <span className={styles.factorLine}>{f.line}</span>
              </span>
            </li>
          ))}
        </ul>

        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          The second lock only decides who can open the app — it never touches
          your photos. Everything stays exactly where it is, and nothing is ever
          deleted.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Both locks, for a year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or month-to-month</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Add the second lock — start ${trialDays}-day free trial`
              : "Add the second lock"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.fdot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.fdot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default TwofoldPaywall
